/**
 * MinerOps API client — handles auth tokens and API calls to FastAPI backend.
 * Falls back gracefully when backend is unavailable (local-only mode).
 */

const API_BASE = '/api';

// Track backend availability
let _backendAvailable = null; // null = unknown, true/false after first check

// Token management
let _token = localStorage.getItem('minerops_token');
let _username = localStorage.getItem('minerops_username');
let _role = localStorage.getItem('minerops_role');

export function getToken() {
  return _token;
}

export function getUsername() {
  return _username;
}

export function getRole() {
  return _role;
}

export function setToken(token, username, role) {
  _token = token;
  _username = username || null;
  _role = role || null;
  if (role) localStorage.setItem('minerops_role', role);
  else localStorage.removeItem('minerops_role');
  if (token) {
    localStorage.setItem('minerops_token', token);
  } else {
    localStorage.removeItem('minerops_token');
  }
  if (username) {
    localStorage.setItem('minerops_username', username);
  } else {
    localStorage.removeItem('minerops_username');
  }
}

export function clearToken() {
  setToken(null, null, null);
}

export function isBackendAvailable() {
  return _backendAvailable;
}

async function apiRequest(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (_token) {
    headers['Authorization'] = `Bearer ${_token}`;
  }
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Check if we got HTML back (means no backend, React is serving the page)
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    _backendAvailable = false;
    throw new Error('Backend unavailable');
  }

  _backendAvailable = true;

  if (res.status === 401) {
    clearToken();
    throw new Error('Incorrect credentials');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'API error');
  }
  return res.json();
}

// ═══════ Auth ═══════

export async function register(username, password) {
  const data = await apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  setToken(data.access_token, data.username, data.role);
  return data;
}

export async function login(username, password) {
  const data = await apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  setToken(data.access_token, data.username, data.role);
  return data;
}

export async function checkBackend() {
  try {
    const res = await fetch(`${API_BASE}/auth/verify`, {
      headers: _token ? { 'Authorization': `Bearer ${_token}` } : {},
    });
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      _backendAvailable = false;
      return { available: false };
    }
    _backendAvailable = true;
    if (res.ok) {
      const data = await res.json();
      if (data.username) _username = data.username;
      if (data.role) { _role = data.role; localStorage.setItem('minerops_role', data.role); }
      return { available: true, authenticated: true, username: data.username, role: data.role, displayName: data.display_name };
    }
    if (res.status === 401) clearToken(); // drop stale/expired token
    return { available: true, authenticated: false };
  } catch {
    _backendAvailable = false;
    return { available: false };
  }
}

export function logout() {
  clearToken();
  window.location.reload();
}

// ═══════ Strategies ═══════

export async function listStrategies() {
  return apiRequest('/strategies');
}

export async function createStrategy(name, strategyData) {
  return apiRequest('/strategies', {
    method: 'POST',
    body: JSON.stringify({ name, strategy_data: strategyData }),
  });
}

export async function getStrategy(id) {
  return apiRequest(`/strategies/${id}`);
}

export async function updateStrategy(id, data) {
  return apiRequest(`/strategies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteStrategy(id) {
  return apiRequest(`/strategies/${id}`, { method: 'DELETE' });
}

// ═══════ Settings ═══════

export async function getAllSettings() {
  return apiRequest('/settings');
}

export async function saveSetting(key, value) {
  return apiRequest(`/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
}

export async function bulkSaveSettings(settings) {
  return apiRequest('/settings/bulk', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
}

// ═══════ Clients (advisor) ═══════

export async function listClients() {
  return apiRequest('/clients');
}

export async function createClient(username, password, displayName) {
  return apiRequest('/clients', {
    method: 'POST',
    body: JSON.stringify({ username, password, display_name: displayName }),
  });
}

export async function updateClient(clientId, { displayName, username } = {}) {
  const body = {};
  if (displayName !== undefined) body.display_name = displayName;
  if (username !== undefined) body.username = username;
  return apiRequest(`/clients/${clientId}`, { method: 'PUT', body: JSON.stringify(body) });
}

export async function resetClientPassword(clientId, password) {
  return apiRequest(`/clients/${clientId}/password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function deleteClient(clientId) {
  return apiRequest(`/clients/${clientId}`, { method: 'DELETE' });
}

// ═══════ Plans ═══════

export async function listPlans(clientId = null) {
  const q = clientId != null ? `?client_id=${clientId}` : '';
  return apiRequest(`/plans${q}`);
}

export async function getPlan(id) {
  return apiRequest(`/plans/${id}`);
}

/** Ask the server to render the plan's report and hand back a PDF file.
 *  Returns { ok: true } or { ok: false, message } — a failed render is expected
 *  on a small server, and the caller offers browser print instead. */
export async function downloadPlanPdf(id, planName = 'plan') {
  const headers = _token ? { Authorization: `Bearer ${_token}` } : {};
  const res = await fetch(`${API_BASE}/plans/${id}/pdf`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    return { ok: false, message: err.detail || 'Could not generate the PDF.' };
  }
  // A 200 is not enough. The backend answers unknown paths with the SPA's
  // index.html so the browser can route client-side, which means an outdated
  // backend returns 200 text/html here — and saving that as a .pdf produces a
  // file the OS cannot open, with nothing to explain why.
  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/pdf')) {
    return {
      ok: false,
      message: 'The server did not return a PDF. It may be running an older version — use Print for now.',
    };
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `MinerOps-${String(planName).replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || 'plan'}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick; revoking synchronously cancels the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return { ok: true };
}

export async function createPlan(name, planData, clientId = null) {
  return apiRequest('/plans', {
    method: 'POST',
    body: JSON.stringify({ name, plan_data: planData, client_id: clientId }),
  });
}

export async function updatePlan(id, { name, planData } = {}) {
  return apiRequest(`/plans/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name, plan_data: planData }),
  });
}

export async function forkPlan(id, name = null) {
  return apiRequest(`/plans/${id}/fork`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function deletePlan(id) {
  return apiRequest(`/plans/${id}`, { method: 'DELETE' });
}
